import React, { useState, useRef, useEffect, useCallback } from 'react'
import './accept-nda.scss'
import { AcceptNDAPresenter } from './accept-nda-presenter'
import { useNavigate } from 'react-router-dom'
import { kycServices } from '../../services'
import { updateConfiguration } from '../../global-state/investor-slice'
import { useDispatch, useSelector } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { RootState } from '../../store'
import { clearPreviousUrl, getPreviousUrl } from '../../lib/utils/helpers'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import {
  applyInvestorKycStateToStore,
  fetchInvestorKycState,
} from '../../lib/utils/sync-investor-kyc-state'
import {
  clearCampaignSignupContext,
  getCampaignSignupContext,
} from '../../lib/utils/campaign-signup-redirect'

const AcceptNDAContainer = () => {
  const [expand, setExpand] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [acceptedTNC, setAcceptedTNC] = useState(false)
  const [acceptedNDA, setAcceptNDA] = useState(false)
  const [isButtonDisabled, setButtonDisabled] = useState(true)
  const [isCheckingExistingNda, setIsCheckingExistingNda] = useState(true)
  const [campaignOpportunityTitle, setCampaignOpportunityTitle] = useState<string | null>(null)

  const { authToken, userId } = useSelector((state: RootState) => state.investor)

  const scrollRef = useRef<null | HTMLDivElement>(null)

  const navigateAfterNdaAcceptance = useCallback(() => {
    const destination = getPreviousUrl() || '/opportunities'
    clearCampaignSignupContext()
    clearPreviousUrl()
    navigate(destination, { replace: true })
  }, [navigate])

  useEffect(() => {
    setCampaignOpportunityTitle(getCampaignSignupContext())
  }, [])

  useEffect(() => {
    const redirectIfNdaAlreadyAccepted = async () => {
      if (!authToken || !userId) {
        setIsCheckingExistingNda(false)
        return
      }

      const kycState = await fetchInvestorKycState(userId, authToken)
      if (kycState?.acceptedNDA) {
        applyInvestorKycStateToStore(dispatch, kycState)
        navigateAfterNdaAcceptance()
        return
      }

      setIsCheckingExistingNda(false)
    }

    redirectIfNdaAlreadyAccepted()
  }, [authToken, userId, dispatch, navigateAfterNdaAcceptance])

  useEffect(() => {
    if (isCheckingExistingNda) return

    dispatch(
      showBanner({
        text: (
          <>
            Please accept the <span className='banner-underline'>Confidentiality Agreement</span> to
            access the Opportunity.
          </>
        ),
        variant: 'warning-banner',
      }),
    )
  }, [dispatch, isCheckingExistingNda])
  const handleButtonClick = () => {
    setExpand(true)
    if (scrollRef && scrollRef.current && scrollRef?.current?.scrollTo) {
      scrollRef?.current?.scrollTo({
        top: scrollRef?.current?.scrollHeight,
        behavior: 'smooth',
      })
    }
  }
  const handleChange = (field: string, val: boolean) => {
    if (field === 'acceptedNDA') setAcceptNDA(val)
    if (field === 'acceptedTNC') setAcceptedTNC(val)
  }

  const handleSelfDeclarationSubmit = async () => {
    const data = {
      acceptedTNC: true,
      acceptedNDA: true,
    }
    try {
      const response = await kycServices.acceptTNCAndNDA_v1(data, {
        Authorization: authToken ? authToken : '',
      })
      if (response && response.status === 200) {
        dispatch(
          updateConfiguration({
            name: 'acceptedNDA',
            value: response.data.is_nda_accepted,
          }),
        )
        dispatch(
          updateConfiguration({
            name: 'kycStatus',
            value: response.data.status,
          }),
        )
        dispatch(
          showBanner({
            text: 'You can now view the opportunity details',
            variant: 'success-banner',
          }),
        )

        logCrmEvent(EngagementEvents.ACCEPTED_CONFIDENTIALITY_AGREEMENT, {
          Authorization: authToken ?? '',
        })

        navigateAfterNdaAcceptance()
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (expand && acceptedTNC && acceptedNDA) {
      setButtonDisabled(false)
    } else {
      setButtonDisabled(true)
    }
  }, [expand, acceptedNDA, acceptedTNC])
  if (isCheckingExistingNda) {
    return null
  }

  return (
    <AcceptNDAPresenter
      isButtonDisabled={isButtonDisabled}
      handleChange={handleChange}
      handleButtonClick={handleButtonClick}
      ref={scrollRef}
      expand={expand}
      acceptedNDA={acceptedNDA}
      acceptedTNC={acceptedTNC}
      handleSelfDeclarationSubmit={handleSelfDeclarationSubmit}
      campaignOpportunityTitle={campaignOpportunityTitle}
    />
  )
}

export { AcceptNDAContainer }
